import type { UnifiedRule } from '@/services/core/BusinessRulesService';
import { effectiveCommands, isOnCommand, parseTrigger } from '@/core/rules/triggerScope';

/**
 * Human-readable "runs on" text for a rule. Domain rules are enforced by a C# site (named in
 * the rule's description); an engine rule runs where its own scope says — the transitions it
 * is mapped to, and the commands an On Command trigger names.
 *
 * C18: the word "always" is deliberately absent. An On Command rule used to run on every
 * command invocation of the entity, so this text said "every command" — true then, and
 * useless: it described a reach nobody had chosen and none could narrow. A command scope is
 * now a list, and this renders the list. Other trigger kinds (e.g. OnValidate) carry no
 * runtime scope of their own and are not rendered.
 */
export function describeRunsOn(rule: UnifiedRule, t: (key: string) => string): string {
  if (rule.executionStrategy === 'domain') {
    return t('businessRules.runsOnDomain');
  }
  const parts: string[] = [];
  if (rule.transitions && rule.transitions.length > 0) {
    parts.push(rule.transitions.map((tr) => `${tr.from} → ${tr.to}`).join(', '));
  }
  const trigger = parseTrigger(rule.trigger);
  if (isOnCommand(trigger)) {
    const commands = effectiveCommands(trigger);
    // A scope-less On Command trigger is refused at the save, so one can only reach here
    // from a row stored before C18 — say that it runs nowhere rather than inventing a reach.
    parts.push(commands.length > 0 ? commands.join(', ') : t('businessRules.runsOnNoCommands'));
  }
  return parts.length > 0 ? parts.join(' · ') : t('businessRules.runsOnUnspecified');
}
